/**
 * Subscription tier metadata. Single source of truth for what each tier
 * unlocks, but NOT for what each tier costs.
 *
 * PRICING LOCALIZATION RULES:
 * - Philippines (PH): Special lower pricing set in Play Console / App Store Connect.
 * - Rest of World: Default USD pricing shown below.
 * - The UI should always prefer RevenueCat's localized `product.priceString`
 *   over these constants when running natively (iOS / Android via Capacitor),
 *   or Stripe's checkout / billing portal copy on web.
 * - These constants are FALLBACKS only — used for web-only Stripe flows when
 *   the store has no opinion, and for non-paywall UI copy like "Pro" badges.
 */
export const TIERS = {
  free: {
    id: "free",
    label: "Free",
    // Fallback price — actual price shown to user comes from RevenueCat / StoreKit localized pricing.
    price: 0,
    priceLabel: "$0",
    concurrent: 1,
    dailyLimit: 1,
    pendingHardCap: 3,
    description: "1 new lead per day, up to 3 pending quotes",
    badge: { bg: "border border-white/10", text: "text-zinc-500" },
  },
  pro: {
    id: "pro",
    label: "Pro",
    // Fallback price — actual price shown to user comes from RevenueCat / StoreKit localized pricing.
    price: 59.99,
    priceLabel: "$59.99",
    concurrent: 3,
    dailyLimit: 3,
    pendingHardCap: 3,
    description: "3 active jobs at a time",
    badge: { bg: "bg-[#FF9500]", text: "text-white" },
  },
  premium: {
    id: "premium",
    label: "Premium",
    // Fallback price — actual price shown to user comes from RevenueCat / StoreKit localized pricing.
    price: 79.99,
    priceLabel: "$79.99",
    concurrent: Infinity,
    dailyLimit: Infinity,
    pendingHardCap: Infinity,
    description: "Unlimited active jobs",
    badge: { bg: "bg-[#FF3B30]", text: "text-white" },
  },
};

export const tierMeta = (tierId) => TIERS[tierId] || TIERS.free;

export const isPaidTier = (tierId) => tierId === "pro" || tierId === "premium";

// Trial length comes from GET /api/config (backend/billing_config.py), not from frontend constants.

/**
 * Resolve the best price string to show the user for a given tier.
 *
 * Preference order:
 *   1. The localized priceString from a RevenueCat package (iOS / Android).
 *      e.g. "$59.99" in the US, "₱2,990" in the Philippines.
 *   2. The fallback USD priceLabel from the TIERS constant.
 *
 * Always returns a string — never null — so the UI never blanks out.
 */
export function displayPriceForTier(tierId, iapPackage) {
  const local = iapPackage?.product?.priceString;
  if (local) return local;
  return tierMeta(tierId).priceLabel;
}
